import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { adminAPI } from '../api/client';
import { Loader, AlertCircle, Clock, MapPin, Camera, RefreshCw, CalendarDays, List } from 'lucide-react';
import { useGlobalSearch } from '../context/SearchContext';
import { matchesGlobalSearch } from '../utils/globalSearch';
import { formatIstDateTime, formatIstMonthYear, istToday } from '../utils/formatIst';
import { resolveUploadUrl } from '../utils/resolveUploadUrl';
import {
  defaultAttendanceMonth,
  daysInMonthYm,
  ymdForDay,
  dayCellStatus,
  monthBoundsYm,
} from '../utils/attendanceMonthly';

function formatDuration(punchIn, punchOut) {
  if (!punchIn || !punchOut) return '—';
  const start = new Date(String(punchIn).replace(' ', 'T'));
  const end = new Date(String(punchOut).replace(' ', 'T'));
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return '—';
  const mins = Math.max(0, Math.round((end - start) / 60000));
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h === 0) return `${m}m`;
  return `${h}h ${m}m`;
}

function formatDurationMinutes(mins) {
  if (mins == null || !Number.isFinite(mins)) return '—';
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h === 0) return `${m}m`;
  return `${h}h ${m}m`;
}

function formatGps(lat, lng) {
  if (lat == null || lng == null) return '—';
  return `${Number(lat).toFixed(4)}, ${Number(lng).toFixed(4)}`;
}

const CELL_STYLES = {
  present: 'bg-green-500',
  active: 'bg-green-400 ring-2 ring-green-600 ring-offset-1',
  absent: 'bg-gray-200',
  future: 'bg-gray-50 border border-gray-100',
};

const VIEW_MODES = [
  { id: 'monthly', label: 'Monthly overview', icon: CalendarDays },
  { id: 'detail', label: 'Rep detail', icon: List },
];

const AttendanceManagement = () => {
  const [reps, setReps] = useState([]);
  const [records, setRecords] = useState([]);
  const [monthSummary, setMonthSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('monthly');
  const [selectedMonth, setSelectedMonth] = useState(defaultAttendanceMonth);
  const [selectedRepId, setSelectedRepId] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [previewSelfie, setPreviewSelfie] = useState(null);
  const { query: globalSearch } = useGlobalSearch();

  const todayYmd = istToday();

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const repsRes = await adminAPI.getAllReps();
      setReps(repsRes.data?.data || []);

      if (viewMode === 'monthly') {
        const sumRes = await adminAPI.getAttendanceMonthSummary({
          month: selectedMonth,
          rep_id: selectedRepId || undefined,
        });
        setMonthSummary(sumRes.data?.data || null);
        setRecords([]);
      } else {
        const attendanceRes = await adminAPI.getAttendance({
          rep_id: selectedRepId || undefined,
          status: statusFilter || undefined,
          from: fromDate || undefined,
          to: toDate || undefined,
          limit: 500,
        });
        setRecords(attendanceRes.data?.data || []);
        setMonthSummary(null);
      }
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        'Failed to load attendance';
      setError(msg);
      setRecords([]);
      setMonthSummary(null);
    } finally {
      setLoading(false);
    }
  }, [viewMode, selectedMonth, selectedRepId, statusFilter, fromDate, toDate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const openRepDayDetail = (repId, dayYmd) => {
    setViewMode('detail');
    setSelectedRepId(String(repId));
    setFromDate(dayYmd);
    setToDate(dayYmd);
    setStatusFilter('');
  };

  const openRepMonthDetail = (repId) => {
    const bounds = monthBoundsYm(selectedMonth);
    setViewMode('detail');
    setSelectedRepId(String(repId));
    setFromDate(bounds?.start || '');
    setToDate(bounds?.end || '');
    setStatusFilter('');
  };

  const filteredRecords = useMemo(() => {
    return records.filter((row) =>
      matchesGlobalSearch(globalSearch, [
        row.rep_name,
        row.rep_email,
        row.rep_phone,
        row.status,
        row.punch_in_time,
        row.punch_out_time,
        formatGps(row.lat_in, row.lng_in),
      ])
    );
  }, [records, globalSearch]);

  const monthlyRows = useMemo(() => {
    const rows = monthSummary?.reps || [];
    if (!globalSearch.trim()) return rows;
    return rows.filter((r) =>
      matchesGlobalSearch(globalSearch, [r.rep_name, r.rep_email])
    );
  }, [monthSummary, globalSearch]);

  const monthDayNums = useMemo(
    () => daysInMonthYm(selectedMonth),
    [selectedMonth]
  );

  const onDutyCount = useMemo(
    () => records.filter((r) => r.status === 'active').length,
    [records]
  );

  const monthlyStats = useMemo(() => {
    if (!monthSummary?.reps?.length) {
      return { totalPresent: 0, repCount: 0, avgDays: 0 };
    }
    const totalPresent = monthSummary.reps.reduce((s, r) => s + (r.days_present || 0), 0);
    const repCount = monthSummary.reps.length;
    return {
      totalPresent,
      repCount,
      avgDays: repCount ? (totalPresent / repCount).toFixed(1) : '0',
    };
  }, [monthSummary]);

  const dayTooltip = (dayEntry) => {
    if (!dayEntry) return 'No punch-in';
    const inT = formatIstDateTime(dayEntry.punch_in_time);
    const outT = dayEntry.punch_out_time
      ? formatIstDateTime(dayEntry.punch_out_time)
      : 'Still on duty';
    const dur =
      dayEntry.duration_minutes != null
        ? formatDurationMinutes(dayEntry.duration_minutes)
        : formatDuration(dayEntry.punch_in_time, dayEntry.punch_out_time);
    return `In: ${inT}\nOut: ${outT}\nDuration: ${dur}`;
  };

  if (loading && records.length === 0 && !monthSummary && !error) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8 flex items-center justify-center min-h-96">
        <div className="text-center">
          <Loader className="w-8 h-8 animate-spin mx-auto mb-3 text-blue-600" />
          <p className="text-gray-600">Loading attendance...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page space-y-6">
      <div className="flex flex-wrap justify-between items-start gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-1">Attendance</h2>
          <p className="text-gray-600">
            Monthly team overview and per-rep punch-in / punch-out detail (selfie + GPS).
          </p>
        </div>
        <button
          type="button"
          onClick={fetchData}
          disabled={loading}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      <div className="flex flex-wrap gap-2">
        {VIEW_MODES.map((mode) => {
          const Icon = mode.icon;
          const active = viewMode === mode.id;
          return (
            <button
              key={mode.id}
              type="button"
              onClick={() => setViewMode(mode.id)}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold border transition ${
                active
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
              }`}
            >
              <Icon className="w-4 h-4" />
              {mode.label}
            </button>
          );
        })}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {viewMode === 'monthly' ? (
          <>
            <div className="bg-white rounded-lg border border-gray-100 p-4 shadow-sm">
              <p className="text-sm text-gray-500">Reps in view</p>
              <p className="text-2xl font-bold text-gray-900">{monthlyStats.repCount}</p>
            </div>
            <div className="bg-white rounded-lg border border-green-100 p-4 shadow-sm">
              <p className="text-sm text-green-700">Total days present</p>
              <p className="text-2xl font-bold text-green-800">{monthlyStats.totalPresent}</p>
            </div>
            <div className="bg-white rounded-lg border border-gray-100 p-4 shadow-sm">
              <p className="text-sm text-gray-500">Avg days / rep</p>
              <p className="text-2xl font-bold text-gray-900">{monthlyStats.avgDays}</p>
            </div>
          </>
        ) : (
          <>
            <div className="bg-white rounded-lg border border-gray-100 p-4 shadow-sm">
              <p className="text-sm text-gray-500">Records shown</p>
              <p className="text-2xl font-bold text-gray-900">{filteredRecords.length}</p>
            </div>
            <div className="bg-white rounded-lg border border-green-100 p-4 shadow-sm">
              <p className="text-sm text-green-700">Currently on duty</p>
              <p className="text-2xl font-bold text-green-800">{onDutyCount}</p>
            </div>
            <div className="bg-white rounded-lg border border-gray-100 p-4 shadow-sm">
              <p className="text-sm text-gray-500">Reps</p>
              <p className="text-2xl font-bold text-gray-900">{reps.length}</p>
            </div>
          </>
        )}
      </div>

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
          <p className="text-red-700">{error}</p>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Representative</label>
            <select
              value={selectedRepId}
              onChange={(e) => setSelectedRepId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            >
              <option value="">All reps</option>
              {reps.map((rep) => (
                <option key={rep.id} value={rep.id}>
                  {rep.name}
                </option>
              ))}
            </select>
          </div>

          {viewMode === 'monthly' ? (
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Month (IST)</label>
              <input
                type="month"
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
          ) : (
            <>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Status</label>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="">All</option>
                  <option value="active">On duty</option>
                  <option value="completed">Completed</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">From date</label>
                <input
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">To date</label>
                <input
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
            </>
          )}
        </div>

        {viewMode === 'monthly' && (
          <p className="text-xs text-gray-500 mt-3">
            {formatIstMonthYear(selectedMonth)} — click a day cell or rep name for punch detail.
          </p>
        )}
      </div>

      {viewMode === 'monthly' && (
        <>
          <div className="flex flex-wrap items-center gap-4 text-xs text-gray-600">
            <span className="inline-flex items-center gap-1.5">
              <span className={`w-3 h-3 rounded-sm ${CELL_STYLES.present}`} /> Present
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className={`w-3 h-3 rounded-sm ${CELL_STYLES.active}`} /> On duty
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className={`w-3 h-3 rounded-sm ${CELL_STYLES.absent}`} /> Absent
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className={`w-3 h-3 rounded-sm ${CELL_STYLES.future}`} /> Future
            </span>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-100 admin-table-shell">
            <div className="admin-table-scroll">
              <table className="admin-data-table-xl text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase sticky left-0 bg-gray-50 z-10 min-w-[140px]">
                      Rep
                    </th>
                    <th className="px-2 py-2 text-center text-xs font-semibold text-gray-600 uppercase w-12">
                      Days
                    </th>
                    {monthDayNums.map((d) => (
                      <th
                        key={d}
                        className={`px-1 py-2 text-center text-xs font-semibold w-7 ${
                          ymdForDay(selectedMonth, d) === todayYmd
                            ? 'text-blue-700 bg-blue-50'
                            : 'text-gray-500'
                        }`}
                      >
                        {d}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {monthlyRows.length === 0 ? (
                    <tr>
                      <td
                        colSpan={monthDayNums.length + 2}
                        className="px-6 py-8 text-center text-gray-600"
                      >
                        No reps match your filters.
                      </td>
                    </tr>
                  ) : (
                    monthlyRows.map((row) => (
                      <tr key={row.rep_id} className="hover:bg-gray-50">
                        <td className="px-3 py-2 sticky left-0 bg-white z-10 border-r border-gray-100">
                          <button
                            type="button"
                            onClick={() => openRepMonthDetail(row.rep_id)}
                            className="text-left font-semibold text-blue-700 hover:underline"
                          >
                            {row.rep_name || '—'}
                          </button>
                          <div className="text-xs text-gray-500 truncate max-w-[160px]">
                            {row.rep_email}
                          </div>
                        </td>
                        <td className="px-2 py-2 text-center font-semibold text-gray-800">
                          {row.days_present ?? 0}
                        </td>
                        {monthDayNums.map((d) => {
                          const dayYmd = ymdForDay(selectedMonth, d);
                          const dayEntry = row.days?.[dayYmd];
                          const status = dayCellStatus(dayEntry, dayYmd, todayYmd);
                          return (
                            <td key={d} className="px-0.5 py-1 text-center">
                              <button
                                type="button"
                                title={dayTooltip(dayEntry)}
                                onClick={() => openRepDayDetail(row.rep_id, dayYmd)}
                                className={`w-5 h-5 rounded-sm mx-auto block ${CELL_STYLES[status]} hover:opacity-80`}
                                aria-label={`${row.rep_name} ${dayYmd}`}
                              />
                            </td>
                          );
                        })}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {viewMode === 'detail' && (
        <>
          {/* Mobile cards */}
          <div className="md:hidden bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden">
            {filteredRecords.length === 0 ? (
              <div className="px-6 py-8 text-center text-gray-600">
                {records.length === 0
                  ? 'No attendance records yet. Reps punch in from the mobile dashboard.'
                  : 'No records match your filters or search.'}
              </div>
            ) : (
              <div className="divide-y divide-gray-200">
                {filteredRecords.map((row) => {
                  const selfieUrl = resolveUploadUrl(row.selfie_path);
                  return (
                    <div key={row.id} className="p-4 space-y-3">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="font-semibold text-gray-900 break-words">{row.rep_name || '—'}</div>
                          <div className="text-xs text-gray-500 break-all">{row.rep_email}</div>
                        </div>
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-semibold shrink-0 ${
                            row.status === 'active'
                              ? 'bg-green-100 text-green-800'
                              : 'bg-gray-100 text-gray-700'
                          }`}
                        >
                          {row.status === 'active' ? 'On duty' : 'Completed'}
                        </span>
                      </div>
                      <div className="grid grid-cols-1 gap-2 text-sm text-gray-700">
                        <div className="flex items-start gap-2">
                          <Clock className="w-4 h-4 text-gray-400 mt-0.5 shrink-0" />
                          <div className="min-w-0">
                            <div><span className="text-gray-500">In:</span> {formatIstDateTime(row.punch_in_time)}</div>
                            <div><span className="text-gray-500">Out:</span> {formatIstDateTime(row.punch_out_time)}</div>
                            <div><span className="text-gray-500">Duration:</span> {formatDuration(row.punch_in_time, row.punch_out_time)}</div>
                          </div>
                        </div>
                        <div className="text-xs text-gray-600 font-mono break-all">
                          <div><span className="text-gray-500 font-sans">GPS in:</span> {formatGps(row.lat_in, row.lng_in)}</div>
                          <div><span className="text-gray-500 font-sans">GPS out:</span> {formatGps(row.lat_out, row.lng_out)}</div>
                        </div>
                      </div>
                      <div>
                        {selfieUrl ? (
                          <button
                            type="button"
                            onClick={() => setPreviewSelfie(selfieUrl)}
                            className="inline-flex items-center gap-1 text-blue-600 hover:underline text-sm"
                          >
                            <Camera className="w-4 h-4" />
                            View selfie
                          </button>
                        ) : (
                          <span className="text-gray-400 text-sm">No selfie</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Desktop table */}
          <div className="hidden md:block bg-white rounded-lg shadow-sm border border-gray-100 admin-table-shell">
            <div className="admin-table-scroll">
              <table className="admin-data-table-xl">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Rep</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Punch in</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Punch out</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Duration</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">GPS in</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">GPS out</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Selfie</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredRecords.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="px-6 py-8 text-center text-gray-600">
                        {records.length === 0
                          ? 'No attendance records yet. Reps punch in from the mobile dashboard.'
                          : 'No records match your filters or search.'}
                      </td>
                    </tr>
                  ) : (
                    filteredRecords.map((row) => {
                      const selfieUrl = resolveUploadUrl(row.selfie_path);
                      return (
                        <tr key={row.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3">
                            <div className="font-semibold text-gray-900">{row.rep_name || '—'}</div>
                            <div className="text-xs text-gray-500">{row.rep_email}</div>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">
                            <span className="inline-flex items-center gap-1">
                              <Clock className="w-3.5 h-3.5 text-gray-400" />
                              {formatIstDateTime(row.punch_in_time)}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">
                            {formatIstDateTime(row.punch_out_time)}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-700">
                            {formatDuration(row.punch_in_time, row.punch_out_time)}
                          </td>
                          <td className="px-4 py-3 text-xs text-gray-600 font-mono">
                            {formatGps(row.lat_in, row.lng_in)}
                          </td>
                          <td className="px-4 py-3 text-xs text-gray-600 font-mono">
                            {formatGps(row.lat_out, row.lng_out)}
                          </td>
                          <td className="px-4 py-3">
                            <span
                              className={`px-2 py-1 rounded-full text-xs font-semibold ${
                                row.status === 'active'
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-gray-100 text-gray-700'
                              }`}
                            >
                              {row.status === 'active' ? 'On duty' : 'Completed'}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            {selfieUrl ? (
                              <button
                                type="button"
                                onClick={() => setPreviewSelfie(selfieUrl)}
                                className="inline-flex items-center gap-1 text-blue-600 hover:underline text-sm"
                              >
                                <Camera className="w-4 h-4" />
                                View
                              </button>
                            ) : (
                              <span className="text-gray-400 text-sm">—</span>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {previewSelfie && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
          onClick={() => setPreviewSelfie(null)}
          onKeyDown={(e) => e.key === 'Escape' && setPreviewSelfie(null)}
          role="presentation"
        >
          <div
            className="bg-white rounded-xl max-w-lg w-full p-4 shadow-xl"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-label="Punch-in selfie"
          >
            <div className="flex justify-between items-center mb-3">
              <h3 className="font-semibold text-gray-900">Punch-in selfie</h3>
              <button
                type="button"
                onClick={() => setPreviewSelfie(null)}
                className="text-gray-500 hover:text-gray-800"
              >
                Close
              </button>
            </div>
            <img
              src={previewSelfie}
              alt="Punch-in selfie"
              className="w-full rounded-lg border border-gray-200"
            />
            <p className="text-xs text-gray-500 mt-2 flex items-center gap-1">
              <MapPin className="w-3 h-3" />
              Date/time and GPS should appear on the image if stamped by the app.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default AttendanceManagement;
